import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext'; // 👈 import
import { Link } from 'react-router-dom';
import { HiTrash, HiMinus, HiPlus, HiArrowLeft } from 'react-icons/hi';

const API =  process.env.REACT_APP_API_URL;

export default function Cart() {
  const { cartItems, removeFromCart, updateQuantity, totalPrice, clearCart } = useCart();
  const { formatCurrency } = useCurrency(); // 👈 use currency

  const getImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/200';
    if (url.startsWith('/uploads/')) return `${API}/images/${url.replace('/uploads/', '')}`;
    return url;
  };

  // –––––– EMPTY CART ––––––
  if (cartItems.length === 0) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-4">Your cart</p>
          <h2 className="text-3xl font-light text-black mb-2">Nothing here yet</h2>
          <p className="text-gray-500 mb-10 text-sm">Add some items to get started.</p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-sm font-medium text-black border-b border-black pb-1 hover:opacity-60 transition"
          >
            Browse products
            <HiArrowLeft size={14} className="rotate-180" />
          </Link>
        </div>
      </div>
    );
  }

  // –––––– CART WITH ITEMS ––––––
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl font-semibold text-black">
          Your Cart <span className="text-gray-400 font-normal text-base ml-1">({cartItems.length})</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ––––– ITEMS LIST ––––– */}
        <div className="lg:col-span-2 space-y-4">
          {cartItems.map(item => (
            <div
              key={item.key}
              className="flex items-center gap-4 p-3 bg-white border border-gray-100 rounded-xl"
            >
              {/* Image */}
              <Link to={`/products/${item.productId}`} className="w-16 h-20 sm:w-20 sm:h-24 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden">
                <img src={getImageUrl(item.image)} alt={item.name} className="w-full h-full object-cover" />
              </Link>

              {/* Info + quantity */}
              <div className="flex-1 min-w-0">
                <Link to={`/products/${item.productId}`} className="text-sm font-semibold text-black hover:underline line-clamp-1">
                  {item.name}
                </Link>
                {item.size && (
                  <p className="text-xs text-gray-500 mt-0.5">{item.size} {item.color && `/ ${item.color}`}</p>
                )}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
                    <button
                      onClick={() => updateQuantity(item.key, Math.max(1, item.quantity - 1))}
                      className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-black hover:bg-gray-50 transition"
                      disabled={item.quantity <= 1}
                    >
                      <HiMinus size={12} />
                    </button>
                    <span className="w-6 text-center text-xs font-medium">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.key, item.quantity + 1)}
                      className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-black hover:bg-gray-50 transition"
                    >
                      <HiPlus size={12} />
                    </button>
                  </div>
                  {/* 👇 Convert price */}
                  <p className="text-sm font-semibold">{formatCurrency(item.price * item.quantity)}</p>
                </div>
              </div>

              {/* Remove */}
              <button
                onClick={() => removeFromCart(item.key)}
                className="text-gray-300 hover:text-red-500 transition p-1"
                title="Remove"
              >
                <HiTrash size={15} />
              </button>
            </div>
          ))}

          {/* Bottom actions */}
          <div className="flex items-center justify-between pt-2">
            <button onClick={clearCart} className="text-xs text-gray-500 hover:text-black underline transition">
              Clear cart
            </button>
          </div>
        </div>

        {/* ––––– ORDER SUMMARY ––––– */}
        <div className="lg:col-span-1">
          <div className="bg-gray-50 rounded-xl p-5 lg:sticky lg:top-24">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-5">Summary</h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">{formatCurrency(totalPrice)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="text-gray-400 text-xs">At checkout</span>
              </div>
            </div>

            <div className="border-t border-gray-200 my-4"></div>

            <div className="flex justify-between text-base font-bold mb-5">
              <span>Total</span>
              <span>{formatCurrency(totalPrice)}</span>
            </div>

            <Link
              to="/checkout"
              className="block w-full bg-black text-white text-center py-2.5 text-sm font-semibold uppercase tracking-wider rounded-lg hover:bg-gray-900 transition active:scale-[0.98]"
            >
              Checkout
            </Link>

            <Link
              to="/products"
              className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:text-black transition"
            >
              <HiArrowLeft size={14} />
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}